/**
 * AiReportPanel
 *
 * Full-featured side drawer for AI Reports.
 * Two internal views:
 *   1. List view – "Generate Report" button + past reports
 *   2. Detail view – rendered report content (executive summary, went well, etc.)
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  AiReport,
  AiReportHistoryItem,
  AiReportRecommendation,
  AiEntitlement,
  getAiEntitlement,
  generateAiReport,
  getAiReportHistory,
  AiQuotaExceededError,
} from '../utils/api';
import { useAuthStore } from '../store/authStore';

const getRecText = (rec: string | AiReportRecommendation) =>
  typeof rec === 'string' ? rec : rec.recommendation;
const getRecRationale = (rec: string | AiReportRecommendation) =>
  typeof rec === 'string' ? undefined : rec.rationale;

interface AiReportPanelProps {
  isOpen: boolean;
  onClose: () => void;
  boardId: string;
  cardCount: number;
  inline?: boolean;
}

type View = { type: 'list' } | { type: 'detail'; report: AiReport; title: string };
type GenerateState =
  | { type: 'idle' }
  | { type: 'loading' }
  | { type: 'error'; message: string }
  | { type: 'quota'; plan: string; limit: number; resetAt: string };

const AiReportPanel: React.FC<AiReportPanelProps> = ({ isOpen, onClose, boardId, cardCount, inline }) => {
  const token = useAuthStore((s) => s.tokens?.access);

  const [view, setView] = useState<View>({ type: 'list' });
  const [entitlement, setEntitlement] = useState<AiEntitlement | null>(null);
  const [reportHistory, setReportHistory] = useState<AiReportHistoryItem[]>([]);
  const [generateState, setGenerateState] = useState<GenerateState>({ type: 'idle' });
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadEntitlement = useCallback(async () => {
    if (!token) return;
    try {
      const data = await getAiEntitlement(token);
      setEntitlement(data);
    } catch {
      /* silent */
    }
  }, [token]);

  const loadHistory = useCallback(async () => {
    if (!token || !boardId) return;
    setHistoryLoading(true);
    try {
      const data = await getAiReportHistory(boardId, token);
      setReportHistory(data);
    } catch {
      /* silent */
    } finally {
      setHistoryLoading(false);
    }
  }, [token, boardId]);

  useEffect(() => {
    if (isOpen) {
      loadEntitlement();
      loadHistory();
      setView({ type: 'list' });
      setGenerateState({ type: 'idle' });
    }
  }, [isOpen, loadEntitlement, loadHistory]);

  const isOver = entitlement ? !entitlement.isUnlimited && entitlement.remaining === 0 : false;
  const hasCards = cardCount > 0;

  const handleGenerate = async () => {
    if (!token || !hasCards) return;
    setGenerateState({ type: 'loading' });
    try {
      const result = await generateAiReport(boardId, token);
      setEntitlement(result.quota);
      if (result.report) {
        const newItem: AiReportHistoryItem = {
          id: Date.now().toString(),
          createdAt: new Date().toISOString(),
          report: result.report,
        };
        setReportHistory((prev) => [newItem, ...prev]);
        setView({ type: 'detail', report: result.report, title: 'Latest Report' });
      }
      setGenerateState({ type: 'idle' });
    } catch (err: unknown) {
      if (err instanceof AiQuotaExceededError) {
        const quotaErr = err;
        setGenerateState({
          type: 'quota',
          plan: quotaErr.plan,
          limit: quotaErr.limit,
          resetAt: quotaErr.resetAt,
        });
        const usedVal = quotaErr.used;
        setEntitlement((prev) => (prev ? { ...prev, remaining: 0, used: usedVal } : prev));
      } else {
        const msg = err instanceof Error ? err.message : 'Something went wrong.';
        setGenerateState({ type: 'error', message: msg });
      }
    }
  };

  const quotaBadgeText = (() => {
    if (!entitlement) return null;
    if (entitlement.isUnlimited) return 'Unlimited';
    if (entitlement.remaining === null) return null;
    return `${entitlement.remaining} remaining`;
  })();

  const panelContent = (
    <>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-white">
          <div className="flex items-center gap-2.5">
            {view.type === 'detail' && (
              <button
                onClick={() => setView({ type: 'list' })}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-colors mr-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-orange-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15M14.25 3.104c.251.023.501.05.75.082M19.8 15l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.607L5 14.5m14.8.5l1.402 1.402c1 1 .03 2.598-1.352 2.598H4.15c-1.38 0-2.35-1.598-1.351-2.598L4.2 14.9" />
              </svg>
            </div>
            <div>
              <h2 className="font-semibold text-slate-900 text-sm leading-tight">
                {view.type === 'list' ? 'AI Reports' : view.title}
              </h2>
              {view.type === 'detail' && view.report.retro_date && (
                <p className="text-xs text-slate-500">{view.report.retro_name} · {view.report.retro_date}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto pr-10">
          {view.type === 'list' ? (
            <div className="p-5 space-y-5">
              {/* Generate Section */}
              <div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-4">
                <h3 className="text-sm font-semibold text-slate-900 mb-1">Generate New Report</h3>
                <p className="text-xs text-slate-500 mb-4">
                  AI analyzes all cards on your board and generates a comprehensive retrospective report.
                </p>

                {!hasCards && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-orange-50 border border-orange-200 mb-4">
                    <svg className="w-4 h-4 text-orange-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <p className="text-xs text-orange-700">Add some cards to your board first before generating a report.</p>
                  </div>
                )}

                {generateState.type === 'quota' && (
                  <div className="p-3 rounded-lg bg-orange-50 border border-orange-200 mb-4">
                    <p className="text-xs text-orange-700 font-medium mb-1">Report Limit Reached</p>
                    <p className="text-xs text-slate-500">
                      Your <span className="font-semibold capitalize">{generateState.plan}</span> plan allows {generateState.limit} reports per period.
                      Resets {new Date(generateState.resetAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}.
                    </p>
                    <a
                      href="/app/subscription"
                      className="inline-block mt-2 text-xs text-orange-500 hover:text-orange-600 font-medium transition-colors"
                    >
                      Upgrade Plan →
                    </a>
                  </div>
                )}

                {generateState.type === 'error' && (
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200 mb-4">
                    <p className="text-xs text-red-600">{generateState.message}</p>
                  </div>
                )}

                <button
                  onClick={handleGenerate}
                  disabled={generateState.type === 'loading' || !hasCards || isOver}
                  className={`w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all text-sm
                    ${!hasCards || isOver
                      ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      : generateState.type === 'loading'
                        ? 'bg-orange-300 text-white cursor-wait'
                        : 'bg-orange-500 hover:bg-orange-600 text-white shadow-sm'
                    }`}
                >
                  {generateState.type === 'loading' ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      Generating Report...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
                      </svg>
                      Generate Report with AI
                    </>
                  )}
                </button>

                {quotaBadgeText && (
                  <p className={`text-xs text-center mt-2 ${isOver ? 'text-red-500' : 'text-slate-500'}`}>
                    {quotaBadgeText}
                  </p>
                )}
              </div>

              {/* Past Reports */}
              <div>
                <h3 className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Past Reports
                  {reportHistory.length > 0 && (
                    <span className="px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 text-[10px] font-bold">
                      {reportHistory.length}
                    </span>
                  )}
                </h3>

                {historyLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-5 h-5 border-2 border-slate-200 border-t-orange-500 rounded-full animate-spin" />
                  </div>
                ) : reportHistory.length === 0 ? (
                  <div className="text-center py-8 px-4">
                    <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                      </svg>
                    </div>
                    <p className="text-sm text-slate-500">No reports yet</p>
                    <p className="text-xs text-slate-400 mt-1">Generate your first AI report above</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {reportHistory.map((item, i) => (
                      <button
                        key={item.id}
                        onClick={() =>
                          setView({
                            type: 'detail',
                            report: item.report,
                            title: i === 0 ? 'Latest Report' : `Report ${reportHistory.length - i}`,
                          })
                        }
                        className="w-full text-left p-3 rounded-xl bg-white border border-slate-100 hover:border-orange-200 hover:bg-orange-50 transition-all group"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2.5">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              i === 0 ? 'bg-orange-100' : 'bg-slate-100'
                            }`}>
                              <svg className={`w-3.5 h-3.5 ${i === 0 ? 'text-orange-600' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-700 group-hover:text-slate-900 transition-colors">
                                {i === 0 ? 'Latest Report' : `Report ${reportHistory.length - i}`}
                              </p>
                              <p className="text-[11px] text-slate-500">
                                {new Date(item.createdAt).toLocaleDateString(undefined, {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </p>
                            </div>
                          </div>
                          <svg className="w-4 h-4 text-slate-400 group-hover:text-orange-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <ReportDetail report={view.report} />
          )}
        </div>
    </>
  );

  if (inline) {
    return (
      <div className="h-full flex flex-col bg-white">
        {panelContent}
      </div>
    );
  }

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />}

      <div
        className={`fixed top-0 right-0 h-full w-[460px] max-w-full bg-white shadow-2xl z-50
          flex flex-col transition-transform duration-300 ease-in-out border-l border-slate-100
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {panelContent}
      </div>
    </>
  );
};

/* ── Report Detail View ──────────────────────────────────────────────────── */

const ReportDetail: React.FC<{ report: AiReport }> = ({ report }) => (
  <div className="p-5 space-y-5">
    <Section title="Executive Summary" icon="📋">
      <p className="text-sm text-slate-700 leading-relaxed">{report.executive_summary}</p>
    </Section>

    {report.went_well && report.went_well.length > 0 && (
      <Section title="Went Well" icon="✅">
        <div className="space-y-2">
          {report.went_well.map((item, i) => (
            <div key={i} className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
              <p className="text-sm text-slate-700">{item.note}</p>
              {item.translation && item.translation !== item.note && (
                <p className="text-xs text-slate-500 mt-1 italic">{item.translation}</p>
              )}
            </div>
          ))}
        </div>
      </Section>
    )}

    {report.to_improve && report.to_improve.length > 0 && (
      <Section title="To Improve" icon="🔧">
        <div className="space-y-2">
          {report.to_improve.map((item, i) => (
            <div key={i} className="rounded-lg border border-orange-200 bg-orange-50 p-3">
              <p className="text-sm text-slate-700">{item.note}</p>
              {item.translation && item.translation !== item.note && (
                <p className="text-xs text-slate-500 mt-1 italic">{item.translation}</p>
              )}
            </div>
          ))}
        </div>
      </Section>
    )}

    {report.action_items && report.action_items.length > 0 && (
      <Section title="Action Items" icon="🚀">
        <div className="space-y-3">
          {report.action_items.map((a, i) => (
            <div key={i} className="rounded-lg border border-orange-200 bg-orange-50 p-3">
              <p className="text-sm font-semibold text-orange-700">{a.title}</p>
              {a.notes && <p className="text-xs text-slate-500 leading-relaxed mt-1">{a.notes}</p>}
              <div className="flex items-center gap-3 mt-2">
                {a.owner && <span className="text-xs text-orange-600">👤 {a.owner}</span>}
                {a.due_date && <span className="text-xs text-slate-500">📅 {a.due_date}</span>}
              </div>
            </div>
          ))}
        </div>
      </Section>
    )}

    {report.recommendations && report.recommendations.length > 0 && (
      <Section title="Recommendations" icon="💡">
        <ul className="space-y-2">
          {report.recommendations.map((rec, i) => (
            <li key={i} className="flex gap-2 text-sm text-slate-700">
              <span className="text-orange-500 mt-0.5 flex-shrink-0">•</span>
              <span className="leading-relaxed">
                {getRecText(rec)}
                {getRecRationale(rec) && (
                  <span className="block text-xs text-slate-500 mt-0.5 italic">{getRecRationale(rec)}</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      </Section>
    )}

    {report.risks && report.risks.length > 0 && (
      <Section title="Risks" icon="⚠️">
        <div className="space-y-2">
          {report.risks.map((r, i) => (
            <div key={i} className="rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-sm text-slate-700 leading-relaxed">{r}</p>
            </div>
          ))}
        </div>
      </Section>
    )}
  </div>
);

const Section: React.FC<{ title: string; icon: string; children: React.ReactNode }> = ({ title, icon, children }) => (
  <div>
    <h3 className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2.5">
      <span>{icon}</span>
      {title}
    </h3>
    {children}
  </div>
);

export default AiReportPanel;
